// Hero1.js - Contains functions for creating a Hero section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'hero1-section';
  const heroContainerClass = 'hero1-container';
  const heroContentClass = 'hero1-content';
  const heroTitleClass = 'hero1-title';
  const heroSubtitleClass = 'hero1-subtitle';
  const heroButtonsClass = 'hero1-buttons';
  const heroPrimaryBtnClass = 'hero1-primary-btn';
  const heroSecondaryBtnClass = 'hero1-secondary-btn';
  const heroImageWrapperClass = 'hero1-image-wrapper';
  const heroImageClass = 'hero1-image';

  // Desktop Styles (default)
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    gap: '40px'
  };

  const heroContentStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '600px'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '48px',
    fontWeight: '700',
    color: '#ffffff',
    lineHeight: '1.2'
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '18px',
    fontWeight: '400',
    color: '#cccccc',
    lineHeight: '1.6',
    maxWidth: '540px'
  };

  const heroButtonsStyles = {
    display: 'flex',
    gap: '16px',
    marginTop: '20px'
  };

  const heroPrimaryBtnStyles = {
    padding: '12px 24px',
    backgroundColor: '#0099ff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const heroSecondaryBtnStyles = {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#ffffff',
    border: '1px solid #4e4e4e',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const heroImageWrapperStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '500px'
  };

  const heroImageStyles = {
    width: '100%',
    maxWidth: '480px',
    borderRadius: '8px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    border: '1px solid #333333'
  };

  // Tablet Styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    padding: '40px 20px',
    gap: '30px'
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '40px'
  };

  const tabletHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '400px'
  };

  // Mobile Styles
  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column-reverse',
    padding: '30px 15px',
    gap: '30px',
    textAlign: 'center'
  };

  const mobileHeroContentStyles = {
    ...heroContentStyles,
    alignItems: 'center'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '32px'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '16px'
  };

  const mobileHeroButtonsStyles = {
    ...heroButtonsStyles,
    flexDirection: 'column',
    width: '100%'
  };

  const mobileHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '100%'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContentClass, heroContentStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSecondaryBtnClass, heroSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageClass, heroImageStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroImageWrapperClass, tabletHeroImageWrapperStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroContentClass, mobileHeroContentStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonsClass, mobileHeroButtonsStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageWrapperClass, mobileHeroImageWrapperStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroContainerClass,
    heroContentClass,
    heroTitleClass,
    heroSubtitleClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroSecondaryBtnClass,
    heroImageWrapperClass,
    heroImageClass
  ]));

  // Generate IDs for all elements
  const heroSectionId = Date.now();
  const containerID = Date.now() + 1;
  const contentID = Date.now() + 2;
  const titleID = Date.now() + 3;
  const subtitleID = Date.now() + 4;
  const buttonsWrapperID = Date.now() + 5;
  const primaryButtonID = Date.now() + 6;
  const secondaryButtonID = Date.now() + 7;
  const imageWrapperID = Date.now() + 8;
  const heroImageID = Date.now() + 9;

  const newElements = [
    // Main Hero Section Container
    {
      id: heroSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Hero Section',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [containerID],
      text: '',
      attributes: {
        'data-element': 'hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    
    // Container for content and image
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: "Hero Container",
      className: heroContainerClass,
      styles: {},
      children: [contentID, imageWrapperID],
      text: '',
      attributes: {
        'data-element': 'hero-container'
      }
    },
    
    // Content area (left side)
    {
      id: contentID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Content',
      parentId: containerID,
      hierarchyName: "Hero Content",
      className: heroContentClass,
      styles: {},
      children: [titleID, subtitleID, buttonsWrapperID],
      text: '',
      attributes: {
        'data-element': 'hero-content'
      }
    },
    
    // Title
    {
      id: titleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: contentID,
      hierarchyName: "Hero Title",
      className: heroTitleClass,
      styles: {},
      children: [],
      text: 'Build Your Future with Our Platform',
      attributes: {
        'data-element': 'hero-title'
      },
      interactions: {
        events: [{
          id: Date.now(),
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-down',
            duration: '1s',
            delay: '0.2s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: contentID,
      hierarchyName: "Hero Subtitle",
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'The fastest and most efficient way to transform your ideas into reality. Start your journey with us today.',
      attributes: {
        'data-element': 'hero-subtitle'
      },
      interactions: {
        events: [{
          id: Date.now() + 1,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '1s',
            delay: '0.4s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Buttons Wrapper
    {
      id: buttonsWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Buttons',
      parentId: contentID,
      hierarchyName: "Hero Buttons",
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID, secondaryButtonID],
      text: '',
      attributes: {
        'data-element': 'hero-buttons'
      }
    },
    
    // Primary Button
    {
      id: primaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Primary Button",
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Get Started',
      attributes: {
        'data-element': 'primary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 2,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '1s',
            delay: '0.6s',
            iterations: '1'
          },
        },
        {
          id: Date.now() + 3,
          type: 'animation',
          action: 'animate',
          trigger: 'onHover',
          value: {
            animation: 'bounce',
            duration: '0.5s',
            delay: '0s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Secondary Button
    {
      id: secondaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Secondary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Secondary Button",
      className: heroSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'Learn More',
      attributes: {
        'data-element': 'secondary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      interactions: {
        events: [{
          id: Date.now() + 4,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'slide-up',
            duration: '1s',
            delay: '0.8s',
            iterations: '1'
          }
        },
        {
          id: Date.now() + 5,
          type: 'animation',
          action: 'animate',
          trigger: 'onHover',
          value: {
            animation: 'bounce',
            duration: '0.5s',
            delay: '0s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Image Wrapper (right side)
    {
      id: imageWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Image Wrapper',
      parentId: containerID,
      hierarchyName: "Hero Image Wrapper",
      className: heroImageWrapperClass,
      styles: {},
      children: [heroImageID],
      text: '',
      attributes: {
        'data-element': 'hero-image-wrapper'
      }
    },
    
    // Hero Image
    {
      id: heroImageID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Hero Image',
      parentId: imageWrapperID,
      hierarchyName: "Hero Image",
      className: heroImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1593720213428-28a5b9e94613?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1770&q=80',
      attributes: {
        'data-element': 'hero-image',
        'alt': 'Hero illustration'
      },
      interactions: {
        events: [{
          id: Date.now() + 6,
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '1.2s',
            delay: '0.3s',
            iterations: '1'
          }
        }]
      }
    }
  ];
  
  // Return all elements
  return {
    heroSection: newElements[0],
    elements: newElements
  };
};

export default createHeroElements; 